import React from 'react';
import { ExternalLink, TrendingUp, MoreHorizontal } from 'lucide-react';
import { Button } from '@shared/components/ui/button';
import { cn } from '@shared/lib/utils';
import type { BalanceCardProps } from './types';

const actionButtonStyles = "h-5 w-5 p-0 bg-black/10 hover:bg-black/20 border border-black/15 transition-all duration-200 rounded-md flex items-center justify-center shrink-0";
const actionIconStyles = "!w-3 !h-3 opacity-60 hover:opacity-80";

export const BalanceCard: React.FC<BalanceCardProps> = ({ balance, onAction }) => {
  const handleAction = (action: string) => {
    onAction?.(action, balance.id);
  };

  return (
    <div
      className={cn(
        "group transition-all duration-200 hover:opacity-90 rounded-lg p-5 h-[190px] flex flex-col relative",
        balance.bgColor
      )}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <h3 className={cn("font-medium text-xs", balance.textColor)}>
          {balance.label}
        </h3>
        <Button 
          variant="ghost" 
          size="icon" 
          className="h-5 w-5 -mt-1 -mr-1 opacity-50 hover:opacity-80 transition-opacity"
          aria-label={`More options for ${balance.label}`}
          onClick={() => handleAction('more')}
        >
          <MoreHorizontal className="h-2.5 w-2.5" />
        </Button>
      </div>

      {/* Main Balance */}
      <div className="mb-2">
        <div className={cn("text-xl font-bold", balance.textColor)}>
          {balance.totalBalance}
        </div>
        <div className="text-xs text-muted-foreground mt-0.5">
          Total Balance
        </div>
      </div>

      {/* Currency Breakdown */}
      <div className="h-[50px] mb-2">
        <div className="grid grid-cols-2 gap-x-2 gap-y-0">
          {balance.balances.map((curr) => (
            <div key={curr.currency} className="flex items-center gap-1.5 h-5">
              <span className="text-[11px] font-medium text-muted-foreground">{curr.currency}</span>
              <span className={cn("text-[11px] font-semibold", balance.textColor)}>
                {curr.formatted}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-1.5 absolute bottom-5 left-5">
        <Button 
          variant="ghost" 
          size="sm" 
          className={actionButtonStyles}
          aria-label={`External link for ${balance.label}`}
          onClick={() => handleAction('external')}
        >
          <ExternalLink className={cn(actionIconStyles, balance.textColor)} />
        </Button>
        <Button 
          variant="ghost" 
          size="sm" 
          className={actionButtonStyles}
          aria-label={`View trends for ${balance.label}`}
          onClick={() => handleAction('trends')}
        >
          <TrendingUp className={cn(actionIconStyles, balance.textColor)} />
        </Button>
      </div>
    </div>
  );
};